import React, { useState, useEffect } from 'react';
import { FaUsers, FaSearch, FaFilter, FaEdit, FaTrash, FaEye, FaSpinner, FaEllipsisV, FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt, FaBriefcase, FaLocationArrow } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { driverService } from '../../api/driverService';
import DriverDetailsModal from '../../components/admin/DriverDetailsModal';
import DriverActionMenu from '../../components/admin/DriverActionMenu';

const AdminDrivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [updatingDriver, setUpdatingDriver] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDriverModal, setShowDriverModal] = useState(false);

  useEffect(() => {
    fetchDrivers();
  }, [pagination.page, statusFilter]);

  useEffect(() => {
    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when searching
        fetchDrivers();
      } else if (searchTerm === '') {
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when clearing search
        fetchDrivers();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(searchTerm && { search: searchTerm })
      };

      const response = await driverService.getAllDrivers(params);
      
      if (response.success) {
        setDrivers(response.data.drivers);
        setPagination(response.data.pagination);
      } else {
        toast.error(response.message || 'Failed to load drivers');
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
      toast.error('Failed to load drivers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleStatusChange = async (driverId, newStatus) => {
    try {
      setUpdatingDriver(driverId);
      
      let response;
      switch (newStatus) {
        case 'active':
          response = await driverService.activateDriver(driverId);
          break;
        case 'suspended':
          response = await driverService.suspendDriver(driverId);
          break;
        case 'inactive':
          response = await driverService.deactivateDriver(driverId);
          break;
        default:
          response = await driverService.updateDriver(driverId, { status: newStatus });
      }
      
      if (response.success) {
        // Update the driver in the local state
        setDrivers(prev => prev.map(driver => 
          driver.id === driverId ? { ...driver, status: newStatus } : driver
        ));
        toast.success(`Driver status updated to ${newStatus}`);
      } else {
        toast.error(response.message || 'Failed to update driver status');
      }
    } catch (error) {
      console.error('Error updating driver status:', error);
      toast.error('Failed to update driver status. Please try again.');
    } finally {
      setUpdatingDriver(null);
    }
  };

  const handleViewDriver = (driver) => {
    console.log('Opening modal for driver:', driver);
    console.log('Driver ID:', driver.id);
    setSelectedDriver(driver);
    setShowDriverModal(true);
  };

  const handleDriverUpdated = (updatedDriver) => {
    // Update the driver in the local state
    setDrivers(prev => prev.map(driver => 
      driver.id === updatedDriver.id ? updatedDriver : driver
    ));
  };

  const handleCloseModal = () => {
    setShowDriverModal(false);
    setSelectedDriver(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Driver Management</h1>
            <p className="text-gray-600">Manage and monitor driver accounts</p>
          </div>
          <button
            onClick={fetchDrivers}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center transition-colors"
          >
            {loading ? (
              <FaSpinner className="animate-spin mr-2" />
            ) : (
              <FaUsers className="mr-2" />
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search drivers by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <div className="sm:w-48">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <div className="sm:w-48">
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            defaultValue="all"
          >
            <option value="all">All Drivers</option>
            <option value="withProfile">With Profile Picture</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </select>
        </div>
        <button
          onClick={() => {
            setSearchTerm('');
            setStatusFilter('all');
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
        >
          Clear Filters
        </button>
      </div>

      {/* Driver Statistics */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FaUsers className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-gray-600">Total Drivers</p>
              <p className="text-lg font-bold text-gray-900">{pagination.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <FaUser className="h-5 w-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-lg font-bold text-gray-900">
                {drivers.filter(d => d.status === 'active').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <FaBriefcase className="h-5 w-5 text-orange-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-gray-600">On Job</p>
              <p className="text-lg font-bold text-gray-900">
                {drivers.filter(d => d.activeJobId).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FaUser className="h-5 w-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-gray-600">With Profile</p>
              <p className="text-lg font-bold text-gray-900">
                {drivers.filter(d => d.profileImageUrl).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Drivers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <FaSpinner className="animate-spin h-8 w-8 text-blue-500" />
            <span className="ml-2 text-gray-600">Loading drivers...</span>
          </div>
        )}
        {!loading && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Driver
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active Job
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Join Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {drivers.map((driver) => (
                <tr key={driver.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        {driver.profileImageUrl ? (
                          <img
                            src={driver.profileImageUrl}
                            alt={`${driver.firstName} ${driver.lastName}`}
                            className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm ${driver.profileImageUrl ? 'hidden' : 'flex'}`}>
                          {driver.firstName?.[0] || 'D'}{driver.lastName?.[0] || 'R'}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {driver.firstName || 'Unknown'} {driver.lastName || 'Driver'}
                        </div>
                        <div className="text-sm text-gray-500">ID: {driver.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center text-sm text-gray-900">
                        <FaEnvelope className="mr-2 text-gray-400" />
                        {driver.email || 'No email'}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <FaPhone className="mr-2 text-gray-400" />
                        {driver.phoneNumber || driver.phone || 'No phone'}
                      </div>
                      {driver.lastKnownLocation && (
                        <div className="flex items-center text-xs text-green-600">
                          <FaLocationArrow className="mr-1" />
                          Location Available
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      driver.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : driver.status === 'inactive'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {driver.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm text-gray-900">{driver.role || 'N/A'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {driver.activeJobId ? 'Yes' : 'No'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {driver.createdAt ? new Date(driver.createdAt).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <DriverActionMenu
                      driver={driver}
                      onView={handleViewDriver}
                      onStatusChange={handleStatusChange}
                      updatingDriver={updatingDriver}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-gray-700">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {drivers.length === 0 && !loading && (
        <div className="text-center py-12">
          <FaUsers className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No drivers found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'No drivers have been registered yet. Drivers will appear here once they register through the mobile app.'
            }
          </p>
        </div>
      )}

      {/* Driver Details Modal */}
      {showDriverModal && selectedDriver && (
        <DriverDetailsModal
          isOpen={showDriverModal}
          onClose={handleCloseModal}
          driverId={selectedDriver.id}
          onDriverUpdated={handleDriverUpdated}
          initialDriverData={selectedDriver}
        />
      )}
      
      {/* Debug info */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white p-4 rounded-lg text-sm opacity-75">
          <div>Modal Open: {showDriverModal ? 'Yes' : 'No'}</div>
          <div>Selected Driver: {selectedDriver ? selectedDriver.id : 'None'}</div>
          <div>Drivers Count: {drivers.length}</div>
        </div>
      )} */}
    </div>
  );
};

export default AdminDrivers; 